import React, { useState, useEffect } from 'react';
import axios from '../config/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { usePageContext } from '../context/PageContext';

const ClientManagement = () => {
  const { setPageTitle } = usePageContext();
  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });
  const { hasPermission } = useAuth();

  useEffect(() => {
    setPageTitle('Client Management');
    if (hasPermission('client', 'read')) {
      fetchClients();
    } else {
      setLoading(false);
    }
  }, [setPageTitle, hasPermission]);

  const fetchClients = async () => {
    try {
      const response = await axios.get('/api/client');
      setClients(response.data);
    } catch (error) {
      toast.error('Error fetching clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      if (editingClient) {
        await axios.put(`/api/client/${editingClient._id}`, formData);
        toast.success('Client updated successfully');
      } else {
        await axios.post('/api/client', formData);
        toast.success('Client created successfully');
      }
      setShowModal(false);
      setEditingClient(null);
      resetForm();
      setSelectedClients([]);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving client');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(selectedClients.map(id => axios.delete(`/api/client/${id}`)));
      toast.success(`${selectedClients.length} clients deleted successfully`);
      setSelectedClients([]);
      setShowDeleteModal(false);
      fetchClients();
    } catch (error) {
      toast.error('Error deleting clients');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', address: '' });
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData(client);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await axios.delete(`/api/client/${id}`);
        toast.success('Client deleted successfully');
        fetchClients();
      } catch (error) {
        toast.error('Error deleting client');
      }
    }
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!hasPermission('client', 'read')) {
    return (
      <div className="alert alert-danger text-center">
        <h4>Access Denied</h4>
        <p>You don't have permission to view clients.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <p className="text-muted mb-0">Manage client information and contact details</p>
          {selectedClients.length > 0 && (
            <div className="d-flex align-items-center gap-2 mt-2">
              <span className="badge bg-primary">{selectedClients.length} selected</span>
              <button className="btn btn-sm btn-danger" onClick={() => setShowDeleteModal(true)}>
                <i className="fas fa-trash me-1"></i>Delete Selected
              </button>
            </div>
          )}
        </div>
        {hasPermission('client', 'create') && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <i className="fas fa-plus"></i> Add Client
          </button>
        )}
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <div className="d-flex align-items-center gap-2">
            <i className="fas fa-search text-muted"></i>
            <input
              type="text"
              className="form-control"
              placeholder="Search clients by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="table-container">
        <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Address</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map(client => (
                <tr key={client._id}>
                  <td className="fw-bold">{client.name}</td>
                  <td>{client.email}</td>
                  <td>{client.phone}</td>
                  <td>{client.address || 'N/A'}</td>
                  <td>
                    <div className="d-flex gap-2">
                      {hasPermission('client', 'update') && (
                        <button 
                          className="btn btn-primary"
                          onClick={() => handleEdit(client)}
                          style={{ padding: '0.25rem 0.5rem', fontSize: '12px' }}
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                      )}
                      {hasPermission('client', 'delete') && (
                        <button 
                          className="btn btn-secondary"
                          onClick={() => handleDelete(client._id)}
                          style={{ padding: '0.25rem 0.5rem', fontSize: '12px' }}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
        </table>
        {filteredClients.length === 0 && (
          <div className="text-center py-5">
            <div style={{ fontSize: '4rem', opacity: 0.3 }}>🏢</div>
            <h5 className="text-muted mt-3">No clients found</h5>
            <p className="text-muted">Add clients to manage your business relationships.</p>
            {hasPermission('client', 'create') && (
              <button className="btn btn-primary mt-3" onClick={() => setShowModal(true)}>
                <i className="fas fa-plus me-2"></i>Add First Client
              </button>
            )}
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingClient ? 'Edit Client' : 'Add Client'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      className="form-control"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Address</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        {editingClient ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      editingClient ? 'Update Client' : 'Create Client'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="text-center">
                  <i className="fas fa-exclamation-triangle text-warning" style={{fontSize: '3rem'}}></i>
                  <h5 className="mt-3">Are you sure?</h5>
                  <p className="text-muted">
                    You are about to delete {selectedClients.length} client(s). This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleBulkDelete}>
                  <i className="fas fa-trash me-2"></i>Delete {selectedClients.length} Client(s)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientManagement;